import React from 'react';
import { motion, useMotionValue, useTransform, MotionValue } from 'motion/react';
import { getSignedMediaUrl } from '@/lib/utils';
import type { AllowedArea } from '@/features/booking/types/eventBookingCategoryTypes';

interface MobileAllowedAreaSlideProps {
  area: AllowedArea;
  imageId: string;
  widthPercent?: MotionValue<number>;
}

export const MobileAllowedAreaSlide: React.FC<MobileAllowedAreaSlideProps> = ({ area, imageId, widthPercent }) => {
  const fallback = useMotionValue(50);
  const wp = widthPercent || fallback;

  // Name fades and slides in as the card expands from Small (10) to Medium (40)
  const nameOpacity = useTransform(wp, [20, 36], [0, 1]);
  const nameY = useTransform(wp, [20, 36], [10, 0]);
  
  // Description fades and slides in as the card expands from Medium (40) to Large (50)
  const descOpacity = useTransform(wp, [42, 48], [0, 1]);
  const descY = useTransform(wp, [42, 48], [8, 0]);

  // Utilize CSS Grid 0fr to 1fr hack to perfectly animate height to content
  // without hardcoding pixels or leaving empty bottom gaps if text is short!
  const gridRows = useTransform(wp, [42, 48], ["0fr", "1fr"]);

  return (
    <div className="relative w-full h-full">
      {/* Background Image */}
      {imageId ? (
        <motion.div
           initial={{ opacity: 0 }}
           animate={{ opacity: 1 }}
           className="absolute inset-0 bg-cover bg-center"
           style={{
             backgroundImage: `url(${getSignedMediaUrl(imageId, 'image').url})`,
           }}
        />
      ) : (
        <div className="absolute inset-0 bg-neutral-800" />
      )}

      {/* Gradient Overlay for Text Readability */}
      <div className="absolute inset-x-0 bottom-0 h-[65%] bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />

      {/* Content Positioned at Bottom Container */}
      <div className="absolute z-10 bottom-4 left-4 right-4 pointer-events-none flex flex-col justify-end">
        {/* Name stays strictly 1 line to prevent vertical jitter/reflows */}
        <motion.h4 
          style={{ opacity: nameOpacity, y: nameY }}
          className="text-white font-bold text-lg drop-shadow-md leading-tight whitespace-nowrap text-ellipsis overflow-hidden w-full"
        >
          {area.name}
        </motion.h4>
        
        {/* CSS Grid dynamically sizes to EXACTLY the children's dimensions. Removes the 'shifted above' look. */}
        <motion.div 
          style={{ display: "grid", gridTemplateRows: gridRows }}
          className="w-[150px] min-[400px]:w-[180px] sm:w-[220px] md:w-[260px] max-w-full"
        >
          <div className="overflow-hidden min-h-0">
            <motion.div style={{ opacity: descOpacity, y: descY }} className="pt-0.5">
              <p className="text-gray-300 text-sm drop-shadow-md line-clamp-2 leading-snug">
                {area.description}
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
